import {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import useReduxKey from './useReduxKey';

import timeslotActions from '../actions/timeslotActions';
import { ResourceTreeNode } from './useLocations';
import { Space, Timeslot } from '../reducers/filter';

export default (
  selectedLocation: ResourceTreeNode|Space,
  selectedGroupSize: number,
  selectedDate: string|Date,
  reservationType: string | null = null,
): { timeslots: Map<string, Timeslot[]>, fetchingSlots: boolean } => {
  const { fetchTimeslots, fetchCachedTimeslots } = timeslotActions();
  const { timeslots } = useReduxKey('timeslots');
  const [fetchingSlots, setFetchingSlots] = useState(false);
  const { model, displayInResourceTimezone } = useReduxKey('configuration');

  const fetchTimeslotsEfficiently = useCallback(
    _.debounce(
      (...args) => fetchTimeslots(...args).finally(() => setFetchingSlots(false)),
      500,
    ),
    [],
  );

  useEffect(() => {
    if (selectedLocation && selectedGroupSize && selectedDate) {
      const cachedTimeslots = fetchCachedTimeslots(
        selectedLocation, selectedGroupSize, selectedDate, reservationType);
      if (cachedTimeslots === null) {
        setFetchingSlots(true);
        fetchTimeslotsEfficiently(
          selectedLocation, selectedGroupSize, selectedDate, reservationType);
      }
    }
  }, [selectedLocation, selectedGroupSize, selectedDate, reservationType]);

  const mergedTimeslots = useMemo(() => {
    // Manually set or guess the time zone
    const targetTimeZone = (selectedLocation?.timezone && displayInResourceTimezone) ? selectedLocation.timezone : dayjs.tz.guess();
    // Parse selectedDate in the target time zone
    const selectedDay = dayjs.tz(selectedDate, 'YYYY-MM-DD', targetTimeZone);
    // Calculate start and end of the selected day in the target time zone
    const startOfSelectedDay = selectedDay.startOf('day');
    const endOfSelectedDay = selectedDay.endOf('day');

    return timeslots
      .map((timeslot) => ({
        ...timeslot,
        // Convert timeslots' start and end to the target time zone
        dStart: dayjs.utc(timeslot.start).tz(targetTimeZone),
        dEnd: dayjs.utc(timeslot.end).tz(targetTimeZone),
        timezone: targetTimeZone,
      }))
      .filter((timeslot) => (
        timeslot.dStart.isSameOrAfter(startOfSelectedDay) // Start is on or after the start of the selected day
        && timeslot.dStart.isBefore(endOfSelectedDay) // Start is before the end of the selected day
        && timeslot.available
      ))
      .reduce((accumulatedTimeslots, timeslot) => {
        const foundTimeSlot = accumulatedTimeslots.find((t) => (
          t.dStart.isSame(timeslot.dStart)
          && t.dEnd.isSame(timeslot.dEnd)
        ));

        const booking = {
          resource_id: timeslot.resource_id,
          booking_id: timeslot.id,
        };

        if (foundTimeSlot) {
          const foundResource = foundTimeSlot.spaces.find(({ resource_id: id }) => (
            id === timeslot.resource_id
          ));

          if (!foundResource) {
            foundTimeSlot.spaces.push(booking);
          }
        } else {
          accumulatedTimeslots.push({
            dStart: timeslot.dStart,
            dEnd: timeslot.dEnd,
            timezone: timeslot.timezone,
            model,
            spaces: [booking],
          });
        }

        return accumulatedTimeslots;
      }, [])
      .sort((a, b) => {
        if (a.dStart < b.dStart) return -1;
        if (a.dStart > b.dStart) return 1;
        if (a.dEnd < b.dEnd) return -1;
        if (a.dEnd > b.dEnd) return 1;

        return 0;
      })
      .reduce((groups, timeslot) => {
        const morning = groups.get('morning');
        const afternoon = groups.get('afternoon');
        const evening = groups.get('evening');

        const localStart = timeslot.dStart; // Already in targetTimeZone
        if (localStart.hour() < 12) {
          morning.push(timeslot);
        } else if (localStart.hour() >= 12 && localStart.hour() < 18) {
          afternoon.push(timeslot);
        } else if (localStart.hour() >= 18) {
          evening.push(timeslot);
        }

        return new Map([
          ['morning', morning],
          ['afternoon', afternoon],
          ['evening', evening],
        ]);
      }, new Map([
        ['morning', []],
        ['afternoon', []],
        ['evening', []],
      ]));
  }, [timeslots, selectedGroupSize, selectedDate, model, reservationType]);

  return { timeslots: mergedTimeslots, fetchingSlots };
};
